import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { PERMISSIONS, WORKFLOW_INBOX_PERMISSIONS } from '../constants/permissions';
import { getPendingApprovalCount } from '../services/workflowService';
import { hasAnyPermission, hasPermission } from '../utils/permissions';
import { useAuth } from './AuthContext';

type PendingRequestCountContextValue = {
  leaveCount: number | null;
  wfhCount: number | null;
  permissionCount: number | null;
  attendanceCorrectionCount: number | null;
  approvalCount: number | null;
  reloadPendingCounts: () => Promise<void>;
};

const PendingRequestCountContext =
  createContext<PendingRequestCountContextValue | null>(null);

async function loadCount(
  enabled: boolean,
  entityType?: string,
): Promise<number | null> {
  if (!enabled) return null;
  try {
    return await getPendingApprovalCount(entityType);
  } catch {
    return null;
  }
}

export function PendingRequestCountProvider({
  children,
}: {
  children: ReactNode;
}) {
  const auth = useAuth();
  const [leaveCount, setLeaveCount] = useState<number | null>(null);
  const [wfhCount, setWfhCount] = useState<number | null>(null);
  const [permissionCount, setPermissionCount] = useState<number | null>(null);
  const [attendanceCorrectionCount, setAttendanceCorrectionCount] =
    useState<number | null>(null);
  const [approvalCount, setApprovalCount] = useState<number | null>(null);
  const requestId = useRef(0);

  const clearPendingCounts = useCallback(() => {
    requestId.current += 1;
    setLeaveCount(null);
    setWfhCount(null);
    setPermissionCount(null);
    setAttendanceCorrectionCount(null);
    setApprovalCount(null);
  }, []);

  const status = auth.status;
  const permissions = auth.permissions;

  const reloadPendingCounts = useCallback(async () => {
    if (status !== 'authenticated') {
      clearPendingCounts();
      return;
    }

    const canLeave = hasPermission(permissions, PERMISSIONS.LEAVE_APPROVE);
    const canWfh = hasPermission(permissions, PERMISSIONS.WFH_APPROVE);
    const canPermission = hasPermission(
      permissions,
      PERMISSIONS.PERMISSION_APPROVE,
    );
    const canCorrection = hasPermission(
      permissions,
      PERMISSIONS.ATTENDANCE_APPROVE,
    );
    const canApprovals = hasAnyPermission(
      permissions,
      WORKFLOW_INBOX_PERMISSIONS,
    );
    if (
      !canLeave &&
      !canWfh &&
      !canPermission &&
      !canCorrection &&
      !canApprovals
    ) {
      clearPendingCounts();
      return;
    }

    const current = ++requestId.current;
    const [leave, wfh, permission, correction, approvals] = await Promise.all([
      loadCount(canLeave, 'LEAVE_APPLICATION'),
      loadCount(canWfh, 'WFH_REQUEST'),
      loadCount(canPermission, 'PERMISSION_REQUEST'),
      loadCount(canCorrection, 'ATTENDANCE_CORRECTION'),
      loadCount(canApprovals),
    ]);
    if (current !== requestId.current) return;

    setLeaveCount(leave);
    setWfhCount(wfh);
    setPermissionCount(permission);
    setAttendanceCorrectionCount(correction);
    setApprovalCount(approvals);
  }, [clearPendingCounts, permissions, status]);

  useEffect(() => {
    if (status !== 'authenticated') {
      clearPendingCounts();
      return;
    }
    reloadPendingCounts().catch(() => undefined);
  }, [clearPendingCounts, reloadPendingCounts, status]);

  const value = useMemo(
    () => ({
      leaveCount,
      wfhCount,
      permissionCount,
      attendanceCorrectionCount,
      approvalCount,
      reloadPendingCounts,
    }),
    [
      leaveCount,
      wfhCount,
      permissionCount,
      attendanceCorrectionCount,
      approvalCount,
      reloadPendingCounts,
    ],
  );

  return (
    <PendingRequestCountContext.Provider value={value}>
      {children}
    </PendingRequestCountContext.Provider>
  );
}

export function usePendingRequestCount(): PendingRequestCountContextValue {
  const value = useContext(PendingRequestCountContext);
  if (!value) {
    throw new Error(
      'usePendingRequestCount must be used within PendingRequestCountProvider',
    );
  }
  return value;
}
